import React from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map } from 'immutable'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_COURSE,
  TYPE_CONTENT_VIDEO,
} from '../../services/content-type/index'
import {
  SERIES_CLICK,
  TITLE_CLICK,
  PLAY_EVENT,
} from '../../services/event-tracking/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT } from '../../services/page-manager/constants'
import {
  handleClick,
} from '../Spotlight/utils'
import WatchAccessDenied from '../WatchAccessDenied/index'
import WatchAccessAllowed from '../WatchAccessAllowed/index'
import WatchAccess, { ACCESS_PREVIEW, ACCESS_FEATURE } from '../WatchAccess/index'
import Link from '../Link/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import Button, { TYPES, SIZES } from '../Button/index'
import PlaylistAddRemove from '../PlaylistAddRemove/index'

/**
 * Play icon and text for Spotlight
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} The play icon and text
 */
function PlayIconText (props) {
  const { staticText } = props

  return (
    <>
      <IconV2 className="spotlight-action-buttons__play-icon" type={ICON_TYPES.CIRCULAR_PLAY} />
      <span className="spotlight-action-buttons__play-text">{staticText.get('watchNow')}</span>
    </>
  )
}

/**
 * Watch/Play button
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Boolean} props.publishedAtDateInFuture - Whether or not the published at date
 *  is in the future
 * @param {Boolean} props.isSeries - Whether or not the content is a series
 * @param {Map} props.auth - The auth data
 * @param {Function} props.upstreamContextOnClick - The function that will set the upstream context
 * @returns {JSX.Element} The watch/play button
 */
function WatchButton (props) {
  const {
    data,
    publishedAtDateInFuture,
    isSeries,
    auth,
    upstreamContextOnClick,
    useGraphQLData,
  } = props
  const preview = data.get('preview')
  const feature = data.get('feature')
  const isPublished = data.get('published')
  const url = data.get('url')

  if (isSeries || !isPublished || publishedAtDateInFuture) {
    return null
  }

  if (useGraphQLData) {
    return (
      <div className="spotlight-action-buttons__play-action">
        {
          feature.get('accessAllowed')
            ? <Link
                to={url}
                data-element="play-button"
                onClick={(e) => handleClick(e, props, PLAY_EVENT, upstreamContextOnClick)}
                query={{ fullplayer: 'feature' }}
                className="spotlight-action-buttons__play-link"
              >
                <PlayIconText {...props} />
              </Link>
            : null
        }
        {
          !feature.get('accessAllowed') && preview.get('id')
            ? <Link
                className="spotlight-action-buttons__play-link"
                to={url}
                query={{ fullplayer: 'preview' }}
              >
                <PlayIconText {...props} />
              </Link>
            : null
        }
        {
          !feature.get('accessAllowed') && !preview.get('id')
            ? <Link className="spotlight-action-buttons__play-link" to={url}>
                <PlayIconText {...props} />
              </Link>
            : null
        }
      </div>
    )
  }

  return (
    <div className="spotlight-action-buttons__play-action">
      <WatchAccess auth={auth} preview={preview} feature={feature}>
        <WatchAccessAllowed access={ACCESS_FEATURE}>
          <Link
            to={url}
            data-element="play-button"
            onClick={(e) => handleClick(e, props, PLAY_EVENT, upstreamContextOnClick)}
            query={{ fullplayer: 'feature' }}
            className="spotlight-action-buttons__play-link"
          >
            <PlayIconText {...props} />
          </Link>
        </WatchAccessAllowed>
        <WatchAccessAllowed access={ACCESS_PREVIEW}>
          <Link
            className="spotlight-action-buttons__play-link"
            to={url}
            query={{ fullplayer: 'preview' }}
          >
            <PlayIconText {...props} />
          </Link>
        </WatchAccessAllowed>
        <WatchAccessDenied>
          <Link className="spotlight-action-buttons__play-link" to={url}>
            <PlayIconText {...props} />
          </Link>
        </WatchAccessDenied>
      </WatchAccess>
    </div>
  )
}

/**
 * View series button
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isSeries - Whether or not the content is a series
 * @param {Function} props.upstreamContextOnClick - The function that will set the upstream context
 * @returns {JSX.Element} Button for viewing the series
 */
function ViewSeriesButton (props) {
  const {
    data,
    staticText,
    isSeries,
    upstreamContextOnClick,
  } = props
  const url = data.get('url')

  if (!isSeries) {
    return null
  }

  return (
    <Button
      type={TYPES.PRIMARY}
      className="spotlight-action-buttons__button"
      url={url}
      onClick={(e) => handleClick(e, props, SERIES_CLICK, upstreamContextOnClick)}
      size={SIZES.XSMALL}
    >
      {staticText.get('viewSeries')}
    </Button>
  )
}

/**
 * Playlist Add/Remove button
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.upstreamContext - The upstream context data
 * @param {Boolean} props.isSeries - Whether or not the content is a series
 * @param {Map} props.auth - The auth data
 * @param {Boolean} props.publishedAtDateInFuture - Whether or not the published at date
 *  is in the future
 * @returns {JSX.Element} The playlist add/remove button
 */
function PlaylistAddRemoveButton (props) {
  const {
    auth,
    upstreamContext,
    data,
    isSeries,
    publishedAtDateInFuture,
    section,
  } = props
  const id = data.get('id')
  const title = data.get('title')
  const isPublished = data.get('published')
  const recommId = Map.isMap(section) ? section.getIn(['data', 'data', 'recommId']) : null

  if (!getAuthIsLoggedIn(auth)) {
    return null
  }

  if (isSeries || !isPublished || publishedAtDateInFuture) {
    return null
  }

  return (
    <div className="spotlight-action-buttons__playlist-btn-container">
      <PlaylistAddRemove
        className="spotlight-action-buttons__playlist-btn"
        upstreamContext={upstreamContext.delete('destId')}
        contentId={id}
        contentTitle={title}
        ga4EventData={{
          content_id: id,
          content_type: data.get('contentType'),
          content_name: title,
          media_id: data.getIn(['feature', 'id']),
          media_language: data.get('mediaLanguage'),
          is_featured_media: true,
          parent_series_name: data.get('seriesTitle'),
          parent_series_id: data.get('seriesId'),
          ui_element: 'Spotlight',
          ...(recommId && { recomm_id: recommId }),
        }}
      />
    </div>
  )
}

/**
 * More details button
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isSeries - Whether or not the content is a series
 * @param {Function} props.upstreamContextOnClick - The function that will set the upstream context
 * @returns {JSX.Element} Button for viewing more details
 */
function MoreDetailsButton (props) {
  const {
    data,
    staticText,
    upstreamContextOnClick,
    isSeries,
  } = props
  const url = data.get('url')

  if (isSeries) {
    return null
  }

  return (
    <Link
      to={url}
      className="spotlight-action-buttons__more-details-btn"
      onClick={(e) => handleClick(e, props, TITLE_CLICK, upstreamContextOnClick)}
    >
      <IconV2 className="spotlight-action-buttons__more-details-icon" type={ICON_TYPES.ELLIPSIS} />
      <span className="spotlight-action-buttons__more-details-text">
        {staticText.get('moreDetails')}
      </span>
    </Link>
  )
}

/**
 * View course button
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.staticText - The static text
 * @param {Function} props.upstreamContextOnClick - The function that will set the upstream context
 * @returns {JSX.Element} Button for viewing the course
 */
function ViewCourseButton (props) {
  const {
    data,
    label,
    staticText,
    payloadType,
    upstreamContextOnClick,
  } = props
  const contentType = data.get('contentType')
  const url = contentType === TYPE_CONTENT_VIDEO
    ? data.getIn(['primaryCourseReference', 'contentTo', 0, 'path'])
    : data.get('url')

  const buttonText = payloadType === PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT
    ? label
    : staticText.get('viewCourse')

  return (
    <Button
      type={TYPES.PRIMARY}
      className="spotlight-action-buttons__course-button"
      url={url}
      onClick={(e) => handleClick(e, props, TITLE_CLICK, upstreamContextOnClick)}
    >
      {buttonText}
    </Button>
  )
}

/**
 * The spotlight action buttons
 * @param {Object} props - The props
 * @param {Map} props.contentType - The type of content
 * @returns {JSX.Element} Action buttons for the spotlight
 */
function SpotlightActionButtons (props) {
  const { contentType, data } = props
  const isVideo = contentType === TYPE_CONTENT_VIDEO
  const isSeries = contentType === TYPE_CONTENT_SERIES
  const isCourse = contentType === TYPE_CONTENT_COURSE
  const primaryCourseReference = data.get('primaryCourseReference')

  if (isCourse || (isVideo && primaryCourseReference && primaryCourseReference.size > 0)) {
    return (
      <div className="spotlight-action-buttons spotlight-action-buttons__course">
        <ViewCourseButton {...props} />
      </div>
    )
  }

  return (
    <div className="spotlight-action-buttons">
      <WatchButton {...props} isSeries={isSeries} />
      <ViewSeriesButton {...props} isSeries={isSeries} />
      <PlaylistAddRemoveButton {...props} isSeries={isSeries} />
      <MoreDetailsButton {...props} isSeries={isSeries} />
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => {
      return {
        staticText: state.staticText.getIn(['data', 'spotlightActionButtons', 'data']),
      }
    },
  ),
)(SpotlightActionButtons)
